import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatPoints } from "@/lib/format";
import { Plus, Trash2, Pencil, RotateCcw } from "lucide-react";
import CreateMarketForm from "@/components/CreateMarketForm";

export default function AdminMarketsTab({ onChange }) {
    const [markets, setMarkets] = useState([]);
    const [cats, setCats] = useState([]);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        const [m, c] = await Promise.all([
            api.get("/markets", { params: { limit: 100 } }),
            api.get("/categories"),
        ]);
        setMarkets(m.data);
        setCats(c.data);
    }, []);
    useEffect(() => { load(); }, [load]);

    const resolveMarket = async (market, optionId, skipApi = false) => {
        try {
            if (!skipApi) {
                await api.post(`/markets/${market.id}/resolve`, { result_option_id: optionId, source: market.resolution_source || "Admin", notes: "" });
                toast.success("Mercado resuelto");
            }
            await load();
            onChange?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        }
    };

    const del = async (m) => {
        try {
            const { data } = await api.delete(`/admin/markets/${m.id}`);
            toast.success(`Mercado eliminado. Reembolsos: ${data.refunded_predictions}`);
            await load();
            onChange?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-sora font-bold text-xl">Mercados</h3>
                <Dialog open={creating} onOpenChange={setCreating}>
                    <DialogTrigger asChild>
                        <Button data-testid="create-market-btn" className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold">
                            <Plus className="w-4 h-4 mr-1.5" /> Nuevo mercado
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-p2w-surface border-p2w-line text-white max-w-xl max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-sora">Crear mercado</DialogTitle>
                        </DialogHeader>
                        <CreateMarketForm
                            categories={cats}
                            onCreated={() => { setCreating(false); load(); onChange?.(); }}
                        />
                    </DialogContent>
                </Dialog>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-p2w-line bg-p2w-surface">
                <table className="w-full text-sm">
                    <thead className="bg-p2w-elevated border-b border-p2w-line text-p2w-gray text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-3">Mercado</th>
                            <th className="text-left px-4 py-3">Categoría</th>
                            <th className="text-left px-4 py-3">Estado</th>
                            <th className="text-right px-4 py-3">Bote</th>
                            <th className="text-left px-4 py-3 min-w-[280px]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {markets.map((m) => (
                            <MarketRow key={m.id} m={m} categories={cats} onResolve={resolveMarket} onDelete={() => del(m)} onUpdated={async () => { await load(); onChange?.(); }} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function MarketRow({ m, categories, onResolve, onDelete, onUpdated }) {
    const canEdit = m.status !== "resolved";
    return (
        <tr className="border-t border-p2w-line" data-testid={`admin-market-row-${m.id}`}>
            <td className="px-4 py-3 text-white max-w-[280px]">{m.title}</td>
            <td className="px-4 py-3 text-p2w-gray text-xs">{m.category?.name || "—"}</td>
            <td className="px-4 py-3">
                <StatusBadge status={m.status} />
                {m.auto_closed && m.status === "closed" && (
                    <div className="text-[9px] text-amber-400 mt-0.5 uppercase tracking-wider">auto-cerrado</div>
                )}
            </td>
            <td className="px-4 py-3 text-right number-tabular text-p2w-green">{formatPoints(m.total_pool)}</td>
            <td className="px-4 py-3">
                <div className="flex items-center gap-2 flex-wrap justify-end">
                    {(m.status === "open" || m.status === "closed") && (
                        <ResolveDialog market={m} onResolve={onResolve} variant={m.status === "closed" ? "pending" : "regular"} />
                    )}
                    {m.status === "resolved" && (
                        <ReResolveDialog market={m} onResolve={onResolve} />
                    )}
                    {canEdit && <EditMarketDialog market={m} categories={categories} onUpdated={onUpdated} />}
                    <DeleteMarketDialog onConfirm={onDelete} id={m.id} market={m} />
                </div>
            </td>
        </tr>
    );
}

function StatusBadge({ status }) {
    const map = {
        open:    { label: "Abierto",   cls: "bg-p2w-green/15 text-p2w-green border-p2w-green/30" },
        closed:  { label: "Cerrado · pendiente",   cls: "bg-amber-400/15 text-amber-400 border-amber-400/30" },
        resolving: { label: "Resolviendo", cls: "bg-blue-400/15 text-blue-400 border-blue-400/30" },
        resolved: { label: "Resuelto", cls: "bg-p2w-gray/15 text-p2w-gray border-p2w-line" },
    };
    const info = map[status] || { label: status, cls: "bg-p2w-gray/15 text-p2w-gray border-p2w-line" };
    return (
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${info.cls}`}>
            {info.label}
        </span>
    );
}

function EditMarketDialog({ market, categories, onUpdated }) {
    const [open, setOpen] = useState(false);
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button data-testid={`edit-market-${market.id}`} className="p-2 rounded-lg text-p2w-blue hover:bg-p2w-blue/10 transition" title="Editar mercado">
                    <Pencil className="w-4 h-4" />
                </button>
            </DialogTrigger>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white max-w-xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-sora">Editar mercado</DialogTitle>
                </DialogHeader>
                <EditMarketForm
                    market={market}
                    categories={categories}
                    onSaved={() => { setOpen(false); onUpdated?.(); }}
                />
            </DialogContent>
        </Dialog>
    );
}

function DeleteMarketDialog({ onConfirm, id, market }) {
    // For resolved markets enforce 8-day cool-down (matches backend rule).
    const isResolved = market?.status === "resolved";
    const { eligible, daysLeft, daysPassed } = (() => {
        if (!isResolved) return { eligible: true, daysLeft: 0, daysPassed: 0 };
        const ra = market?.resolved_at;
        if (!ra) return { eligible: false, daysLeft: 8, daysPassed: 0 };
        const resolvedDate = new Date(ra);
        const passed = (Date.now() - resolvedDate.getTime()) / 86400000;
        const left = Math.max(0, Math.ceil(8 - passed));
        return { eligible: passed >= 8, daysLeft: left, daysPassed: passed };
    })();

    const locked = isResolved && !eligible;

    return (
        <AlertDialog>
            <AlertDialogTrigger asChild>
                <button
                    data-testid={`delete-market-${id}`}
                    disabled={locked}
                    title={locked ? `Faltan ~${daysLeft} día(s) para poder eliminar este mercado resuelto` : "Eliminar mercado"}
                    className={`p-2 rounded-lg transition ${locked
                        ? "text-p2w-gray/40 cursor-not-allowed"
                        : "text-p2w-red hover:bg-p2w-red/10"}`}
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-p2w-surface border-p2w-line text-white">
                <AlertDialogHeader>
                    <AlertDialogTitle className="font-sora">
                        {isResolved ? "¿Eliminar mercado resuelto?" : "¿Eliminar mercado?"}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-p2w-gray space-y-2">
                        {isResolved ? (
                            <>
                                <span className="block">
                                    Han pasado <b className="text-white">{Math.floor(daysPassed)}</b> día(s) desde la
                                    resolución. La operación ya está cerrada y los saldos están liquidados.
                                </span>
                                <span className="block text-xs">
                                    Esta acción borra el mercado, sus opciones, predicciones y registro de resolución.
                                    Los movimientos en el ledger (pagos, comisiones, reembolsos) <b className="text-white">se conservan</b> para auditoría.
                                </span>
                            </>
                        ) : (
                            <span>Se reembolsarán todas las predicciones activas en el bote.</span>
                        )}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel className="bg-p2w-elevated border-p2w-line text-white hover:bg-p2w-line hover:text-white">Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                        className="bg-p2w-red text-white hover:bg-p2w-red/90"
                        onClick={onConfirm}
                        data-testid={`confirm-delete-market-${id}`}
                    >
                        Eliminar
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

function ResolveDialog({ market, onResolve, variant = "regular" }) {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(1); // 1 = select option, 2 = confirm
    const [optId, setOptId] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const winningOpt = market.options.find((o) => o.id === optId);
    const reset = () => { setStep(1); setOptId(""); };
    const close = (v) => { setOpen(v); if (!v) reset(); };

    const submit = async () => {
        if (!optId) return;
        setSubmitting(true);
        try {
            await onResolve(market, optId);
            setOpen(false);
            reset();
        } finally {
            setSubmitting(false);
        }
    };

    const isPending = variant === "pending";
    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogTrigger asChild>
                <Button
                    size="sm"
                    data-testid={`open-resolve-${market.id}`}
                    className={isPending
                        ? "bg-amber-400 text-black hover:bg-amber-400/90 font-semibold h-9"
                        : "bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold h-9"
                    }
                >
                    {isPending ? "Resolver pendiente" : "Resolver"}
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-sora">{step === 1 ? "Seleccionar respuesta ganadora" : "Confirmar resolución"}</DialogTitle>
                    <DialogDescription className="text-p2w-gray text-xs">
                        {step === 1
                            ? "Elige la opción que ganó. Continuarás a un paso de confirmación antes de liquidar el pozo."
                            : "Revisa la selección. Esta acción liquidará el pozo y pagará a los ganadores."}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="rounded-xl bg-p2w-elevated border border-p2w-line p-3">
                        <div className="text-[10px] uppercase tracking-wider text-p2w-gray">Mercado</div>
                        <div className="font-semibold text-white mt-0.5">{market.title}</div>
                        <div className="text-xs text-p2w-gray mt-1 number-tabular">Pozo: <span className="text-p2w-green">{formatPoints(market.total_pool)}</span> pts</div>
                    </div>

                    {step === 1 ? (
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">Respuesta ganadora *</Label>
                            <div className="mt-2 space-y-2">
                                {market.options.map((o) => (
                                    <button
                                        key={o.id}
                                        onClick={() => setOptId(o.id)}
                                        data-testid={`resolve-option-${o.id}`}
                                        className={`w-full text-left px-4 py-3 rounded-lg border transition flex items-center justify-between ${
                                            optId === o.id
                                                ? "border-p2w-green bg-p2w-green/10"
                                                : "border-p2w-line bg-p2w-bg/40 hover:border-p2w-green/40"
                                        }`}
                                    >
                                        <span className="font-medium text-white">{o.label}</span>
                                        <span className={`text-xs ${optId === o.id ? "text-p2w-green font-bold" : "text-p2w-gray"}`}>
                                            {optId === o.id ? "✓ seleccionada" : `${formatPoints(o.current_pool || 0)} pts`}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-amber-400/40 bg-amber-400/5 p-4">
                            <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">⚠ Re-confirma la acción</div>
                            <div className="mt-2 text-sm text-white">
                                Vas a marcar como respuesta ganadora:
                                <div className="mt-2 inline-block px-3 py-1.5 rounded-lg bg-p2w-green/15 border border-p2w-green/40 text-p2w-green font-sora font-bold">
                                    {winningOpt?.label}
                                </div>
                            </div>
                            <p className="mt-3 text-xs text-p2w-gray">
                                Esto liquidará el pozo, pagará a los ganadores y acreditará la comisión a la plataforma.
                                Si te equivocas, podrás editar la resolución después.
                            </p>
                        </div>
                    )}
                </div>
                <DialogFooter className="gap-2">
                    {step === 1 ? (
                        <>
                            <Button variant="outline" onClick={() => close(false)} className="border-p2w-line bg-p2w-elevated text-white">Cancelar</Button>
                            <Button
                                disabled={!optId}
                                onClick={() => setStep(2)}
                                data-testid={`resolve-next-${market.id}`}
                                className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold"
                            >
                                Continuar →
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => setStep(1)} className="border-p2w-line bg-p2w-elevated text-white">← Atrás</Button>
                            <Button
                                onClick={submit}
                                disabled={submitting}
                                data-testid={`resolve-confirm-${market.id}`}
                                className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold"
                            >
                                {submitting ? "Resolviendo…" : "Confirmar y resolver"}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReResolveDialog({ market, onResolve }) {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [optId, setOptId] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const currentOpt = market.options.find((o) => o.id === market.result_option_id);
    const newOpt = market.options.find((o) => o.id === optId);
    const close = (v) => { setOpen(v); if (!v) { setStep(1); setOptId(""); } };

    const submit = async () => {
        if (!optId) return;
        setSubmitting(true);
        try {
            await api.post(`/markets/${market.id}/re-resolve`, {
                result_option_id: optId,
                source: market.resolution_source || "Admin (edit)",
                notes: "Resolución editada por admin",
            });
            toast.success("Resolución actualizada — pagos recalculados");
            setOpen(false);
            setStep(1); setOptId("");
            // Trigger reload via parent
            await onResolve?.(market, null, true);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al re-resolver");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogTrigger asChild>
                <button
                    data-testid={`edit-resolution-${market.id}`}
                    title="Editar resolución (cambiar respuesta ganadora)"
                    className="p-2 rounded-lg text-amber-400 hover:bg-amber-400/10 transition"
                >
                    <RotateCcw className="w-4 h-4" />
                </button>
            </DialogTrigger>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-sora text-amber-400">Editar resolución</DialogTitle>
                    <DialogDescription className="text-p2w-gray text-xs">
                        Cambia la respuesta ganadora si la elegiste por error. Se reversarán los pagos previos y se aplicarán los nuevos automáticamente.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="rounded-xl bg-p2w-elevated border border-p2w-line p-3">
                        <div className="text-[10px] uppercase tracking-wider text-p2w-gray">Mercado</div>
                        <div className="font-semibold text-white mt-0.5">{market.title}</div>
                        <div className="mt-2 flex items-center gap-2 text-xs">
                            <span className="text-p2w-gray">Ganadora actual:</span>
                            <span className="px-2 py-0.5 rounded bg-p2w-green/15 border border-p2w-green/40 text-p2w-green font-semibold">{currentOpt?.label || "—"}</span>
                        </div>
                    </div>

                    {step === 1 ? (
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">Nueva respuesta ganadora *</Label>
                            <div className="mt-2 space-y-2">
                                {market.options.filter((o) => o.id !== market.result_option_id).map((o) => (
                                    <button
                                        key={o.id}
                                        onClick={() => setOptId(o.id)}
                                        data-testid={`re-resolve-option-${o.id}`}
                                        className={`w-full text-left px-4 py-3 rounded-lg border transition flex items-center justify-between ${
                                            optId === o.id
                                                ? "border-amber-400 bg-amber-400/10"
                                                : "border-p2w-line bg-p2w-bg/40 hover:border-amber-400/40"
                                        }`}
                                    >
                                        <span className="font-medium text-white">{o.label}</span>
                                        <span className={`text-xs ${optId === o.id ? "text-amber-400 font-bold" : "text-p2w-gray"}`}>
                                            {optId === o.id ? "✓ seleccionada" : "cambiar a esta"}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-rose-400/40 bg-rose-400/5 p-4 text-sm">
                            <div className="text-[10px] uppercase tracking-wider text-rose-400 font-bold">⚠ Acción crítica</div>
                            <p className="mt-2 text-white">
                                Cambiar la ganadora de <span className="font-bold">{currentOpt?.label}</span> a <span className="font-bold text-amber-400">{newOpt?.label}</span>.
                            </p>
                            <p className="mt-3 text-xs text-p2w-gray leading-relaxed">
                                Esto revierte todos los pagos previos (descuenta a los ganadores anteriores, devuelve fees a la Caja),
                                y vuelve a pagar a los nuevos ganadores. Los saldos se ajustan automáticamente.
                            </p>
                        </div>
                    )}
                </div>
                <DialogFooter className="gap-2">
                    {step === 1 ? (
                        <>
                            <Button variant="outline" onClick={() => close(false)} className="border-p2w-line bg-p2w-elevated text-white">Cancelar</Button>
                            <Button
                                disabled={!optId}
                                onClick={() => setStep(2)}
                                data-testid={`re-resolve-next-${market.id}`}
                                className="bg-amber-400 text-black hover:bg-amber-400/90 font-semibold"
                            >
                                Continuar →
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => setStep(1)} className="border-p2w-line bg-p2w-elevated text-white">← Atrás</Button>
                            <Button
                                onClick={submit}
                                disabled={submitting}
                                data-testid={`re-resolve-confirm-${market.id}`}
                                className="bg-rose-500 text-white hover:bg-rose-500/90 font-semibold"
                            >
                                {submitting ? "Re-resolviendo…" : "Confirmar cambio"}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditMarketForm({ market, categories, onSaved }) {
    // Convert ISO close date to <input type="datetime-local"> value
    const toLocal = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        const tzo = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - tzo).toISOString().slice(0, 16);
    };

    const [form, setForm] = useState({
        title: market.title || "",
        description: market.description || "",
        category_id: market.category?.id || market.category_id || "",
        resolution_source: market.resolution_source || "",
        image_url: market.image_url || "",
        tags: (market.tags || []).join(", "),
        is_featured: !!market.is_featured,
        is_live: !!market.is_live,
        closes_at_local: toLocal(market.closes_at),
        options: (market.options || []).map((o) => ({ id: o.id, label: o.label, pool: o.current_pool || 0 })),
    });
    const [saving, setSaving] = useState(false);

    const setOptLabel = (i, label) => {
        const opts = [...form.options];
        opts[i] = { ...opts[i], label };
        setForm({ ...form, options: opts });
    };

    const removeOpt = (i) => {
        const opts = form.options.filter((_, idx) => idx !== i);
        setForm({ ...form, options: opts });
    };

    const addOpt = () => {
        setForm({ ...form, options: [...form.options, { id: null, label: "" }] });
    };

    const submit = async () => {
        const cleanOpts = form.options
            .map((o) => ({ id: o.id || null, label: (o.label || "").trim() }))
            .filter((o) => o.label.length > 0);
        if (cleanOpts.length < 2) {
            toast.error("Mínimo 2 opciones");
            return;
        }
        const closesIso = form.closes_at_local
            ? new Date(form.closes_at_local).toISOString()
            : null;
        const payload = {
            title: form.title.trim(),
            description: form.description,
            category_id: form.category_id,
            resolution_source: form.resolution_source,
            image_url: form.image_url || null,
            tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
            is_featured: form.is_featured,
            is_live: form.is_live,
            options: cleanOpts.map((o) => (o.id ? { id: o.id, label: o.label } : { label: o.label })),
        };
        if (closesIso) payload.closes_at = closesIso;
        setSaving(true);
        try {
            await api.patch(`/admin/markets/${market.id}`, payload);
            toast.success("Mercado actualizado");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-3">
            <Field label="Pregunta del mercado" testid="edit-market-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
            <div>
                <Label className="text-xs uppercase tracking-widest text-p2w-gray">Descripción</Label>
                <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="edit-market-desc"
                    className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                />
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs uppercase tracking-widest text-p2w-gray">Categoría</Label>
                    <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                        <SelectTrigger className="mt-2 bg-p2w-elevated border-p2w-line text-white" data-testid="edit-market-cat">
                            <SelectValue placeholder="Categoría" />
                        </SelectTrigger>
                        <SelectContent className="bg-p2w-elevated border-p2w-line text-white">
                            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-p2w-gray">Cierra el</Label>
                    <Input
                        type="datetime-local"
                        value={form.closes_at_local}
                        onChange={(e) => setForm({ ...form, closes_at_local: e.target.value })}
                        data-testid="edit-market-closes"
                        className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                    />
                </div>
            </div>

            <div>
                <Label className="text-xs uppercase tracking-widest text-p2w-gray">Opciones</Label>
                <div className="mt-2 space-y-2">
                    {form.options.map((opt, i) => {
                        const hasPool = (opt.pool || 0) > 0;
                        return (
                            <div key={opt.id || `new-${i}`} className="flex items-center gap-2">
                                <Input
                                    value={opt.label}
                                    onChange={(e) => setOptLabel(i, e.target.value)}
                                    data-testid={`edit-market-option-${i}`}
                                    className="bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                                />
                                {hasPool && (
                                    <span className="text-[10px] uppercase tracking-wider text-p2w-amber whitespace-nowrap" title="Tiene predicciones activas">
                                        con bote
                                    </span>
                                )}
                                {form.options.length > 2 && !hasPool && (
                                    <button
                                        onClick={() => removeOpt(i)}
                                        data-testid={`edit-market-remove-option-${i}`}
                                        className="p-2 rounded-lg text-p2w-red hover:bg-p2w-red/10"
                                        title="Eliminar opción"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    <Button
                        type="button"
                        variant="outline"
                        onClick={addOpt}
                        data-testid="edit-market-add-option"
                        className="border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line hover:text-white text-xs h-8"
                    >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Añadir opción
                    </Button>
                </div>
                <p className="mt-1.5 text-xs text-p2w-gray">
                    Las opciones con apuestas activas no se pueden eliminar; sí puedes corregir su nombre.
                </p>
            </div>

            <Field label="Fuente de resolución" testid="edit-market-source" value={form.resolution_source} onChange={(v) => setForm({ ...form, resolution_source: v })} />
            <Field label="Tags (separados por coma)" testid="edit-market-tags" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} />
            <Field label="URL imagen (opcional)" testid="edit-market-img" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />

            <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} data-testid="edit-market-featured" className="accent-p2w-green" />
                    <span className="text-white">Destacado</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.is_live} onChange={(e) => setForm({ ...form, is_live: e.target.checked })} data-testid="edit-market-live" className="accent-p2w-green" />
                    <span className="text-white">En vivo</span>
                </label>
            </div>

            <DialogFooter className="mt-3">
                <Button onClick={submit} disabled={saving} data-testid="edit-market-save" className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold">
                    {saving ? "Guardando…" : "Guardar cambios"}
                </Button>
            </DialogFooter>
        </div>
    );
}

function Field({ label, value, onChange, testid }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-widest text-p2w-gray">{label}</Label>
            <Input
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
            />
        </div>
    );
}
